/**
 * Tragezeitbegrenzung fuer Atemschutz — und was sie fuer die Bauzeit bedeutet.
 *
 * Grundlage: DGUV Regel 112-190 „Benutzung von Atemschutzgeraeten", Anhang 2.
 * Kennt weder DOM noch Supabase noch React.
 *
 * ═══ WARUM DAS EIN EIGENES MODUL IST UND KEINE FUSSNOTE ═══════════════════
 * Die BG BAU fuehrt „fehlende oder unzureichende Tragezeitbegrenzungen" als
 * eigenen Mangel (Check 17). Der Grund ist nicht Formalismus: Die Werte des
 * Anhangs 2 gelten fuer MITTLERE Arbeitsschwere und etwa Raumtemperatur. Auf
 * einer Sommerbaustelle, im Schwarzbereich, unter Chemikalienschutzkleidung
 * gilt keine dieser beiden Voraussetzungen — und die zulaessige Tragedauer
 * sinkt entsprechend.
 *
 * Daraus folgt eine Zahl, die den Arbeitsschutz verlaesst: die nutzbare
 * Arbeitszeit je Schicht. Ein Plan, der Vollmaske vorschreibt und zugleich
 * Tagesleistungen ohne Erholungszeiten unterstellt, ist in sich
 * widerspruechlich — und die Baustelle loest diesen Widerspruch dann zulasten
 * der Maske. Deshalb rechnet dieses Modul die Leistungsminderung aus und
 * sagt sie an.
 *
 * ═══ WAS HIER BEWUSST NICHT STEHT ═════════════════════════════════════════
 * Die vollstaendige Tabelle des Anhangs 2 mit ihren Minutenwerten je
 * Geraetegruppe ist NICHT eingepflegt. Erfundene oder aus zweiter Hand
 * uebernommene Minutenwerte waeren hier gefaehrlicher als gar keine: sie
 * saehen aus wie die Regel und wuerden ungeprueft in gedruckte Plaene
 * wandern.
 *
 * Stattdessen traegt jede Geraetegruppe ihren Wert mit `beleg`:
 *   'regel'     — im Regelwerk oder in der Fachliteratur ausdruecklich so
 *                 benannt und hier belegt,
 *   'zu_pruefen'— Platzhalter; die Oberflaeche verlangt die Eingabe aus dem
 *                 Anhang 2, bevor der Wert in den Plan darf.
 * `istBelegt()` sagt, ob gerechnet werden darf.
 */

/** Fassung dieses Katalogs. */
export const TRAGEZEIT_KATALOG_VERSION = '2026-08-22';

export type Beleg = 'regel' | 'zu_pruefen';

export interface Geraetegruppe {
    key: string;
    label: string;
    /** Zulaessige Einsatzdauer in Minuten bei mittlerer Arbeitsschwere und
     *  etwa Raumtemperatur. */
    basis_min: number | null;
    /** Mindesterholungsdauer nach einem Einsatz in Minuten. */
    erholung_min: number | null;
    /** Zulaessige Einsaetze je Schicht. */
    einsaetze: number | null;
    beleg: Beleg;
    quelle: string;
    hinweis?: string;
}

/**
 * Die Geraetegruppen.
 *
 * Belegt ist der Eckwert, der in der Praxis am haeufigsten gebraucht wird
 * und den auch der Musterplan nennt: hoechstens zwei Stunden Tragezeit,
 * danach mindestens 30 Minuten Erholung. Alles Feinere gehoert aus dem
 * Anhang 2 uebernommen.
 */
export const GERAETEGRUPPEN: Geraetegruppe[] = [
    {
        key: 'filter_ffp',
        label: 'Filtrierende Halbmaske (FFP)',
        basis_min: null,
        erholung_min: null,
        einsaetze: null,
        beleg: 'zu_pruefen',
        quelle: 'DGUV Regel 112-190 Anhang 2',
        hinweis: 'Werte aus Anhang 2 übernehmen.',
    },
    {
        key: 'filter_halbmaske',
        label: 'Halbmaske mit Filter',
        basis_min: null,
        erholung_min: null,
        einsaetze: null,
        beleg: 'zu_pruefen',
        quelle: 'DGUV Regel 112-190 Anhang 2',
        hinweis: 'Werte aus Anhang 2 übernehmen.',
    },
    {
        key: 'filter_vollmaske',
        label: 'Vollmaske mit Filter',
        basis_min: 120,
        erholung_min: 30,
        einsaetze: null,
        beleg: 'regel',
        quelle: 'DGUV Regel 112-190 Anhang 2 (Höchsttragedauer 2 h, Erholung mind. 30 min)',
        hinweis: 'Richtwert für mittlere Arbeitsschwere und etwa Raumtemperatur — auf der '
            + 'Baustelle im Schwarzbereich ist beides zu prüfen.',
    },
    {
        key: 'geblaese_haube',
        label: 'Gebläsefiltergerät mit Haube (TH3P)',
        basis_min: null,
        erholung_min: null,
        einsaetze: null,
        beleg: 'zu_pruefen',
        quelle: 'DGUV Regel 112-190 Anhang 2',
        hinweis: 'Gebläsegeräte entlasten die Atmung, nicht zwingend den Kreislauf. '
            + 'Werte aus Anhang 2 übernehmen.',
    },
    {
        key: 'isolier_schlauch',
        label: 'Druckluftschlauchgerät',
        basis_min: null,
        erholung_min: null,
        einsaetze: null,
        beleg: 'zu_pruefen',
        quelle: 'DGUV Regel 112-190 Anhang 2',
    },
    {
        key: 'isolier_behaelter',
        label: 'Behältergerät (umluftunabhängig)',
        basis_min: null,
        erholung_min: null,
        einsaetze: null,
        beleg: 'zu_pruefen',
        quelle: 'DGUV Regel 112-190 Anhang 2',
        hinweis: 'Zusätzlich begrenzt durch den Luftvorrat.',
    },
];

const NACH_KEY = new Map(GERAETEGRUPPEN.map(g => [g.key, g]));

export const geraetegruppe = (key: string): Geraetegruppe | null => NACH_KEY.get(key) ?? null;

/** Ob fuer diese Gruppe belegte Werte vorliegen und damit gerechnet werden
 *  darf. */
export const istBelegt = (key: string): boolean => {
    const g = NACH_KEY.get(key);
    return !!g && g.beleg === 'regel' && g.basis_min !== null;
};

// ═══════════════════════════════════════════════════════════════════════════
// KORREKTUREN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Umstand, der die zulaessige Tragedauer verkuerzt.
 *
 * `faktor` ist ein Multiplikator auf die Basisdauer. Die Faktoren sind
 * BEWUSST konservativ und ausdruecklich als betriebliche Festlegung
 * gekennzeichnet, nicht als Regelwerkswert: Anhang 2 nennt Korrekturen
 * qualitativ, die zahlenmaessige Umsetzung ist Sache der
 * Gefaehrdungsbeurteilung. Wer andere Faktoren ansetzt, traegt sie am Plan
 * ein — sie erscheinen dort samt Begruendung.
 */
export interface Korrektur {
    key: string;
    grund: string;
    faktor: number;
    beleg: Beleg;
}

export const KORREKTUREN: Korrektur[] = [
    {
        key: 'schwere_arbeit',
        grund: 'schwere körperliche Arbeit (über mittlere Arbeitsschwere hinaus)',
        faktor: 0.75,
        beleg: 'zu_pruefen',
    },
    {
        key: 'hitze',
        grund: 'erhöhte Umgebungstemperatur (Sommer, Sonneneinstrahlung, Enge)',
        faktor: 0.75,
        beleg: 'zu_pruefen',
    },
    {
        key: 'chemikalienschutzkleidung',
        grund: 'gleichzeitiges Tragen von Chemikalienschutzkleidung (Wärmestau)',
        faktor: 0.75,
        beleg: 'zu_pruefen',
    },
    {
        key: 'enge',
        grund: 'Arbeiten in engen Räumen, Schächten oder Gruben',
        faktor: 0.85,
        beleg: 'zu_pruefen',
    },
];

const KORREKTUR_NACH_KEY = new Map(KORREKTUREN.map(k => [k.key, k]));

// ═══════════════════════════════════════════════════════════════════════════
// RECHNUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface TragezeitErgebnis {
    /** Zulaessige Einsatzdauer nach Korrektur, in Minuten. `null`, wenn die
     *  Basiswerte nicht belegt sind. */
    ergebnis_min: number | null;
    erholung_min: number | null;
    basis_min: number | null;
    angewandte_korrekturen: { grund: string; faktor: number }[];
    /** Was der Bearbeiter wissen muss — auch der Satz „hier fehlt der Wert". */
    hinweise: string[];
    /** Ob das Ergebnis in den Plan darf. */
    verwendbar: boolean;
}

/**
 * Rechnet die zulaessige Tragedauer aus.
 *
 * Die Korrekturen wirken MULTIPLIKATIV: zwei Umstaende, die je fuer sich ein
 * Viertel kosten, kosten zusammen mehr als ein Viertel. Additive Abzuege
 * saehen bei mehreren Belastungen schnell nach null aus und wuerden dann
 * ignoriert.
 *
 * Das Ergebnis wird ABGERUNDET auf volle fuenf Minuten. Eine Tragezeit von
 * „67,5 Minuten" behauptet eine Genauigkeit, die die Ausgangswerte nicht
 * haben — und auf der Baustelle wird ohnehin auf die naechste Pause gerundet.
 */
export const berechneTragezeit = (
    gruppeKey: string,
    korrekturKeys: string[],
): TragezeitErgebnis => {
    const g = NACH_KEY.get(gruppeKey);
    const hinweise: string[] = [];

    if (!g) {
        return {
            ergebnis_min: null, erholung_min: null, basis_min: null,
            angewandte_korrekturen: [],
            hinweise: ['Unbekannte Gerätegruppe.'],
            verwendbar: false,
        };
    }

    const angewandt = korrekturKeys
        .map(k => KORREKTUR_NACH_KEY.get(k))
        .filter((k): k is Korrektur => !!k)
        .map(k => ({ grund: k.grund, faktor: k.faktor }));

    if (g.basis_min === null) {
        hinweise.push(
            `Für „${g.label}“ ist kein Wert hinterlegt. Basisdauer und Erholungszeit aus `
            + 'DGUV Regel 112-190 Anhang 2 eintragen — ohne sie wird nichts gerechnet.',
        );
        return {
            ergebnis_min: null, erholung_min: g.erholung_min, basis_min: null,
            angewandte_korrekturen: angewandt, hinweise, verwendbar: false,
        };
    }

    const faktor = angewandt.reduce((f, k) => f * k.faktor, 1);
    const roh = g.basis_min * faktor;
    const ergebnis = Math.max(5, Math.floor(roh / 5) * 5);

    if (g.hinweis) hinweise.push(g.hinweis);
    if (angewandt.length === 0) {
        hinweise.push(
            'Keine Korrektur angesetzt. Der Basiswert gilt für mittlere Arbeitsschwere und '
            + 'etwa Raumtemperatur — trifft das hier wirklich zu?',
        );
    }
    if (angewandt.some(k => k.faktor < 1) && ergebnis < g.basis_min) {
        hinweise.push(
            `Die Tragedauer sinkt von ${g.basis_min} auf ${ergebnis} Minuten. Diese `
            + 'Leistungsminderung gehört in die Bauzeitenplanung.',
        );
    }

    return {
        ergebnis_min: ergebnis,
        erholung_min: g.erholung_min,
        basis_min: g.basis_min,
        angewandte_korrekturen: angewandt,
        hinweise,
        verwendbar: true,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// WAS DAS FUER DIE SCHICHT BEDEUTET
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ab welchem Anteil ausgefallener Schichtzeit auf ein anderes Arbeitsverfahren
 * hingewiesen wird.
 *
 * Ein Drittel, und das ist keine runde Zahl aus Bequemlichkeit: die Minderung
 * ergibt sich aus Erholung / (Einsatz + Erholung). Schon bei 120 Minuten
 * Einsatz und 30 Minuten Erholung liegt sie bei einem Viertel; sinkt die
 * Tragedauer durch Hitze und Schutzkleidung auf 50 Minuten, sind es 37 %.
 * Genau dort kippt die Frage: Wer ein Drittel der Schicht mit Erholung
 * verbringt, sollte nicht die Pausen besser planen, sondern die Exposition
 * beseitigen — technische Massnahmen gehen der PSA vor (TOP).
 */
export const SCHWELLE_MINDERUNG = 1 / 3;

export interface SchichtErgebnis {
    /** Zahl der moeglichen Einsaetze in der Schicht. */
    einsaetze: number;
    /** Tatsaechlich unter Atemschutz nutzbare Zeit in Minuten. */
    nutzbar_min: number;
    /** Anteil der Schicht, der als Erholung ausfaellt (0..1). */
    minderung: number;
    hinweise: string[];
}

/**
 * Rechnet aus, wie viel einer Schicht unter Atemschutz nutzbar bleibt.
 *
 * Genau die Zahl, die laut Check 17 in die Bauzeitenplanung gehoert und in
 * der Praxis fehlt. Das Modell ist einfach und absichtlich nicht feiner:
 * Einsatz, Erholung, Einsatz, Erholung … bis die Schicht voll ist. Der
 * letzte Erholungsblock faellt ans Schichtende und wird nicht mitgezaehlt.
 *
 * `deckel` begrenzt zusaetzlich die Zahl der Einsaetze, wenn Anhang 2 fuer
 * die Geraetegruppe eine Hoechstzahl vorgibt.
 */
export const schichtLeistung = (
    tragezeit: TragezeitErgebnis,
    schicht_min: number,
    deckel?: number | null,
): SchichtErgebnis => {
    const hinweise: string[] = [];
    if (!tragezeit.verwendbar || !tragezeit.ergebnis_min) {
        return {
            einsaetze: 0, nutzbar_min: 0, minderung: 0,
            hinweise: ['Ohne belegte Tragezeit lässt sich die Schichtleistung nicht angeben.'],
        };
    }

    const einsatz = tragezeit.ergebnis_min;
    const erholung = tragezeit.erholung_min ?? 0;
    const block = einsatz + erholung;

    // Volle Bloecke plus ein moeglicher letzter Einsatz ohne folgende Erholung.
    let einsaetze = Math.floor(schicht_min / block);
    let verbraucht = einsaetze * block;
    if (schicht_min - verbraucht >= einsatz) {
        einsaetze += 1;
        verbraucht += einsatz;
    }

    if (deckel && einsaetze > deckel) {
        hinweise.push(
            `Rechnerisch waeren ${einsaetze} Einsaetze möglich, Anhang 2 lässt aber `
            + `${deckel} zu. Es gilt die kleinere Zahl.`,
        );
        einsaetze = deckel;
    }

    const nutzbar = einsaetze * einsatz;
    const minderung = schicht_min > 0 ? 1 - nutzbar / schicht_min : 0;

    if (minderung >= SCHWELLE_MINDERUNG) {
        hinweise.push(
            `Nur ${nutzbar} von ${schicht_min} Minuten sind unter Atemschutz nutzbar `
            + `(${Math.round(minderung * 100)} % Minderung). Bei dieser Groessenordnung ist zu `
            + 'prüfen, ob ein anderes Arbeitsverfahren die Exposition ganz vermeidet — '
            + 'technische Maßnahmen gehen der PSA vor.',
        );
    }

    return { einsaetze, nutzbar_min: nutzbar, minderung, hinweise };
};
